import api from '../../utils/api';
import type { WhatsAppCampaign, WhatsAppRecipientPreview, WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import { isApprovedWhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';

const BASE = '/master-admin/whatsapp';

export async function fetchWhatsAppCampaigns(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  campaignType?: string;
  automationStatus?: string;
}) {
  const response = await api.get<{ success: boolean; data: WhatsAppCampaign[]; pagination: object }>(`${BASE}/campaigns`, {
    params,
  });
  return response.data;
}

export async function getWhatsAppCampaign(id: string) {
  const response = await api.get<{ success: boolean; data: WhatsAppCampaign }>(`${BASE}/campaigns/${id}`);
  return response.data;
}

export async function createWhatsAppCampaign(payload: Partial<WhatsAppCampaign>) {
  const response = await api.post<{ success: boolean; data: WhatsAppCampaign }>(`${BASE}/campaigns`, payload);
  return response.data;
}

export async function updateWhatsAppCampaign(id: string, payload: Partial<WhatsAppCampaign>) {
  const response = await api.put<{ success: boolean; data: WhatsAppCampaign }>(`${BASE}/campaigns/${id}`, payload);
  return response.data;
}

export async function deleteWhatsAppCampaign(id: string) {
  const response = await api.delete(`${BASE}/campaigns/${id}`);
  return response.data;
}

export async function previewWhatsAppCampaign(payload: Partial<WhatsAppCampaign> & { previewLimit?: number }) {
  const response = await api.post<{
    success: boolean;
    data: { total: number; participants: WhatsAppRecipientPreview[] };
  }>(`${BASE}/campaigns/preview`, payload);
  return response.data;
}

export async function estimateWhatsAppRecipients(payload: Partial<WhatsAppCampaign>) {
  const response = await api.post<{ success: boolean; data: { estimatedRecipients: number } }>(
    `${BASE}/campaigns/estimate-recipients`,
    payload
  );
  return response.data;
}

export async function searchWhatsAppRecipients(q: string, type: 'all' | 'user' | 'vendor' | 'doctor' = 'all') {
  const response = await api.get<{ success: boolean; data: WhatsAppRecipientPreview[] }>(`${BASE}/recipients/search`, {
    params: { q, type },
  });
  return response.data;
}

export async function sendWhatsAppCampaign(
  id: string,
  opts?: {
    scheduleMode?: 'now' | 'later';
    scheduledAt?: string;
    scheduleTimezone?: string;
    weeklyCapAction?: 'exclude' | 'schedule_next_week' | 'override';
    scheduleNextWeekAt?: string;
    overrideWeeklyCap?: boolean;
  }
) {
  const body = {
    ...opts,
    weeklyCapAction:
      opts?.weeklyCapAction ||
      (opts?.overrideWeeklyCap ? 'override' : undefined),
    overrideWeeklyCap: opts?.overrideWeeklyCap,
  };
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: WhatsAppCampaign;
    policySummary?: WhatsAppCampaignSendPreflight['summary'];
    sendPlan?: WhatsAppCampaignSendPlan;
  }>(`${BASE}/campaigns/${id}/send`, body);
  return response.data;
}

export type WhatsAppCampaignSendPlan = {
  recipientCount: number;
  phaseSize: number;
  phaseCount: number;
  waveDelaySeconds: number;
  sendWindowHours: number;
  estimatedCompletionHours: number;
};

export type WhatsAppCampaignSendPreflight = {
  policy: {
    phaseSize?: number;
    sendWindowHours?: number;
    weeklyCapDays?: number;
    nonReaderExcludeDays?: number;
    excludeNonReaders?: boolean;
    enforceWeeklyCap?: boolean;
  };
  summary: {
    total: number;
    ready: number;
    deferredWeekly: number;
    excludedNonReaders: number;
    skipped: number;
  };
  weeklyConflicts: Array<{ phone: string; campaignId?: string | null; sentAt?: string | null; templateName?: string | null }>;
  sendPlan: WhatsAppCampaignSendPlan;
  scheduleNextWeekAt?: string;
  ready: unknown[];
  deferredWeekly: unknown[];
  excludedNonReaders: unknown[];
  skipped: unknown[];
};

export async function preflightWhatsAppCampaignSend(
  id: string,
  opts?: { weeklyCapAction?: string; overrideWeeklyCap?: boolean }
) {
  const response = await api.post<{ success: boolean; data: WhatsAppCampaignSendPreflight }>(
    `${BASE}/campaigns/${id}/send-preflight`,
    opts || {}
  );
  return response.data;
}

/** Queue retry via ReminderEngine (550 contacts / 1.5 min waves until all attempted). */
export async function retryFailedWhatsAppCampaign(
  id: string,
  opts?: { onlyRateLimited?: boolean }
) {
  const response = await api.post<{
    success: boolean;
    queued?: boolean;
    alreadyRunning?: boolean;
    empty?: boolean;
    message?: string;
    data: WhatsAppCampaign;
    batch?: WhatsAppOutboundBatchSummary;
    sendPlan?: WhatsAppOutboundSendPlan;
    sendResult?: {
      recipientCount: number;
      totalSent: number;
      totalFailed: number;
      skippedAlreadySent?: number;
      rateLimitedFailures?: number;
    };
  }>(`${BASE}/campaigns/${id}/retry-failed`, opts || {});
  return response.data;
}

export type WhatsAppOutboundBatchSummary = {
  id: string;
  scope: string;
  campaignId?: string | null;
  reason: string;
  status: string;
  batchSize: number;
  waveDelaySeconds: number;
  messageCategory?: string;
  templateName?: string | null;
  currentWaveIndex: number;
  totalQueued: number;
  totalAttempted: number;
  totalSent: number;
  totalFailed: number;
  totalSkipped: number;
  remaining: number;
  progressPct: number;
  estimatedCostInr: number;
  projectedCostInr: number;
  costPerMessageInr: number;
  nextRunAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  lastError?: string | null;
};

export type WhatsAppOutboundSendPlan = {
  queued: number;
  wavesPlanned: number;
  batchSize: number;
  waveDelaySeconds: number;
  costPerMessageInr: number;
  projectedCostInr: number;
  alreadyRunning?: boolean;
};

export type WhatsAppOutboundBatchDetail = {
  batch: WhatsAppOutboundBatchSummary;
  waves: Array<{
    id: string;
    waveIndex: number;
    status: string;
    attempted: number;
    sent: number;
    failed: number;
    skipped: number;
    rateLimited: number;
    estimatedCostInr: number;
    sampleErrors?: Array<{ phone?: string; reason?: string }>;
    startedAt?: string | null;
    finishedAt?: string | null;
  }>;
  itemStatusCounts: Record<string, number>;
};

export async function fetchCampaignOutboundBatches(id: string, params?: { limit?: number }) {
  const response = await api.get<{ success: boolean; data: WhatsAppOutboundBatchSummary[] }>(
    `${BASE}/campaigns/${id}/outbound-batches`,
    { params }
  );
  return response.data;
}

export async function fetchOutboundBatchDetail(batchId: string) {
  const response = await api.get<{ success: boolean; data: WhatsAppOutboundBatchDetail }>(
    `${BASE}/outbound-batches/${batchId}`
  );
  return response.data;
}

export async function enqueueCampaignMetaLimitRetarget(
  id: string,
  body?: { mode?: 'immediate' | 'schedule' | 'policy24h'; sendAt?: string }
) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: {
      campaignId: string;
      mode?: string;
      metaLimitedUnique: number;
      enqueued: number;
      skipped: number;
      firstSendAt: string;
      maxAttempts: number;
      intervalHours: number | null;
      delayMs?: number;
    };
  }>(`${BASE}/campaigns/${id}/meta-limit-retarget`, body || {});
  return response.data;
}

export async function fetchCampaignMetaLimitRetries(
  id: string,
  params?: { limit?: number; offset?: number; status?: string; search?: string }
) {
  const response = await api.get<{
    success: boolean;
    data: {
      items: Array<{
        id: string;
        phone: string;
        status: string;
        attemptCount: number;
        maxAttempts: number;
        sendAt?: string | null;
        lastError?: string | null;
        lastErrorCode?: string | null;
        meta?: { attemptsLog?: Array<{ attempt: number; at: string; sent: boolean; reason?: string | null }>; scheduleMode?: string; scheduledAt?: string };
      }>;
      stats: Record<string, number>;
      schedule?: {
        hasQueue: boolean;
        pending: number;
        isScheduled: boolean;
        nextSendAt?: string | null;
        scheduleMode?: string | null;
      };
      analysis?: MetaLimitedCohortAnalysis | null;
      attemptAnalysis?: MetaLimitAttemptAnalysis | null;
      pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
    };
  }>(`${BASE}/campaigns/${id}/meta-limit-retarget`, { params });
  return response.data;
}

export async function updateCampaignMetaLimitSchedule(
  id: string,
  body: { mode: 'immediate' | 'schedule'; sendAt?: string }
) {
  const response = await api.patch<{ success: boolean; message?: string; data: unknown }>(
    `${BASE}/campaigns/${id}/meta-limit-retarget/schedule`,
    body
  );
  return response.data;
}

export async function stopCampaignMetaLimitRetarget(id: string) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: { campaignId: string; cancelled: number; reason?: string };
  }>(`${BASE}/campaigns/${id}/meta-limit-retarget/stop`, {});
  return response.data;
}

export async function enqueueCampaignRcsRetarget(
  id: string,
  body?: { mode?: 'immediate' | 'schedule'; sendAt?: string; rcsTemplateName?: string }
) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: {
      campaignId: string;
      mode?: string;
      rcsTemplateName?: string;
      otherFailureUnique: number;
      enqueued: number;
      skipped: number;
      firstSendAt: string;
      rcsEnabled?: boolean;
    };
  }>(`${BASE}/campaigns/${id}/rcs-retarget`, body || {});
  return response.data;
}

export async function fetchCampaignRcsRetries(
  id: string,
  params?: { limit?: number; offset?: number; logLimit?: number; status?: string; search?: string }
) {
  const response = await api.get<{
    success: boolean;
    data: {
      items: Array<{
        id: string;
        phone: string;
        status: string;
        attemptCount: number;
        maxAttempts: number;
        sendAt?: string | null;
        lastError?: string | null;
        rcsTemplateName?: string;
        meta?: { scheduleMode?: string; scheduledAt?: string };
      }>;
      logs: Array<{
        id: string;
        phone: string;
        status: string;
        rcsTemplateName?: string;
        interaktMessageId?: string | null;
        errorMessage?: string | null;
        sentAt?: string | null;
        createdAt?: string | null;
      }>;
      stats: Record<string, number>;
      schedule?: {
        hasQueue: boolean;
        pending: number;
        isScheduled: boolean;
        nextSendAt?: string | null;
        scheduleMode?: string | null;
      };
      pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
    };
  }>(`${BASE}/campaigns/${id}/rcs-retarget`, { params });
  return response.data;
}

export async function updateCampaignRcsSchedule(
  id: string,
  body: { mode: 'immediate' | 'schedule'; sendAt?: string }
) {
  const response = await api.patch<{ success: boolean; message?: string; data: unknown }>(
    `${BASE}/campaigns/${id}/rcs-retarget/schedule`,
    body
  );
  return response.data;
}

export async function stopCampaignRcsRetarget(id: string) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: { campaignId: string; cancelled: number; reason?: string };
  }>(`${BASE}/campaigns/${id}/rcs-retarget/stop`, {});
  return response.data;
}

export async function fetchWhatsAppTemplates(opts?: { allStatuses?: boolean }) {
  const response = await api.get<{ success: boolean; data: WhatsAppTemplate[] }>(`${BASE}/templates`, {
    params: opts?.allStatuses ? { allStatuses: true } : undefined,
  });
  return response.data;
}

/** Approved templates only — for send, campaigns, and settings pickers. */
export async function fetchApprovedWhatsAppTemplates() {
  const res = await fetchWhatsAppTemplates();
  return {
    ...res,
    data: (res.data || []).filter((t) => isApprovedWhatsAppTemplate(t)),
  };
}

export async function fetchWhatsAppAudiences() {
  const response = await api.get<{ success: boolean; data: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppCampaignMeta }>(
    `${BASE}/meta/audiences`
  );
  return response.data;
}

export async function activateWhatsAppCampaign(id: string) {
  const response = await api.post<{ success: boolean; data: WhatsAppCampaign; message?: string }>(
    `${BASE}/campaigns/${id}/activate`
  );
  return response.data;
}

export async function pauseWhatsAppCampaign(id: string) {
  const response = await api.post<{ success: boolean; data: WhatsAppCampaign; message?: string }>(
    `${BASE}/campaigns/${id}/pause`
  );
  return response.data;
}

export async function fetchWhatsAppCampaignJobs(id: string, params?: { limit?: number; status?: string }) {
  const response = await api.get<{ success: boolean; data: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppCampaignSendJob[] }>(
    `${BASE}/campaigns/${id}/jobs`,
    { params }
  );
  return response.data;
}

export type MetaLimitAttemptRow = {
  attempt: number;
  label: string;
  pending: number;
  attempted: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  deliveryRatePct: number;
};

export type MetaLimitAttemptAnalysis = {
  maxAttempts: number;
  totalQueued: number;
  attempts: MetaLimitAttemptRow[];
};

export type MetaLimitedCohortAnalysis = {
  attempted: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  stillLimited: number;
  recovered: number;
  queued?: number;
  deliveryRatePct: number;
  readRatePct: number;
  recoveryRatePct: number;
  statusBreakdown?: Record<string, number>;
  source?: string;
  note?: string;
};

export type WhatsAppCampaignAnalysis = {
  campaignId: string;
  analysis: {
    attempted: number;
    sent: number;
    delivered: number;
    read: number;
    replied: number;
    failed: number;
    limitedByMeta: number;
    otherFailures: number;
    metaLimited?: MetaLimitedCohortAnalysis | null;
    queued: number;
    deliveryRatePct: number;
    readRatePct: number;
    replyRatePct: number;
    estimatedCostInr: number;
    messageCategory: string;
    statusBreakdown: Record<string, number>;
  };
  variables: Array<{
    slot: number;
    label: string;
    contextKey?: string | null;
    sourceType: string;
    staticValue?: string;
    deepLinkCampaignId?: string | null;
    sampleValue: string;
  }>;
  participants: Array<{
    id: string;
    name: string;
    phone: string;
    recipientType?: string;
    status: string;
    bodyValues: string[];
    errorMessage?: string | null;
    sentAt?: string | null;
    deliveredAt?: string | null;
    readAt?: string | null;
  }>;
  participantTotal: number;
  participantFilter?: string | null;
  jobs: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppCampaignSendJob[];
  interakt: {
    interaktApiCampaignId?: string | null;
    dashboardUrl?: string;
    errors?: unknown[];
  };
  policy?: {
    policyCounts?: Record<string, number>;
    sendPlan?: WhatsAppCampaignSendPlan | null;
    batches?: Array<{
      id: string;
      status: string;
      waveDelaySeconds?: number;
      phaseCount?: number;
      policySummary?: WhatsAppCampaignSendPreflight['summary'];
    }>;
  } | null;
  sendPolicy?: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppCampaignSendPolicy | null;
  retryTemplatePlan?: string[] | null;
  retryHeaderMediaPlan?: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppCampaign['retryHeaderMediaPlan'];
};

export async function fetchWhatsAppCampaignAnalysis(
  id: string,
  params?: { limit?: number; lite?: boolean; participantFilter?: string }
) {
  const response = await api.get<{ success: boolean; data: WhatsAppCampaignAnalysis }>(
    `${BASE}/campaigns/${id}/analysis`,
    {
      params: {
        limit: params?.limit,
        lite: params?.lite ? true : undefined,
        participantFilter: params?.participantFilter || undefined,
      },
    }
  );
  return response.data;
}

// ─── Inbox ───────────────────────────────────────────────────────────────────

export async function fetchInboxConversations(params?: {
  search?: string;
  filter?: 'all' | 'unread' | 'outbound';
  limit?: number;
  offset?: number;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppConversation[];
    meta?: { limit: number; offset: number; hasMore: boolean; returned: number };
  }>(`${BASE}/inbox/conversations`, { params });
  return response.data;
}

export async function fetchInboxThread(countryCode: string, phoneNumber: string) {
  const cc = encodeURIComponent(countryCode);
  const pn = encodeURIComponent(phoneNumber);
  const response = await api.get<{
    success: boolean;
    data: {
      contact: import('../../models/MasterAdmin/whatsappHub').WhatsAppThreadContact;
      messages: import('../../models/MasterAdmin/whatsappHub').WhatsAppThreadMessage[];
    };
  }>(`${BASE}/inbox/${cc}/${pn}`);
  return response.data;
}

export async function fetchInboxContactInsights(countryCode: string, phoneNumber: string) {
  const cc = encodeURIComponent(countryCode);
  const pn = encodeURIComponent(phoneNumber);
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppContactInsights;
  }>(`${BASE}/inbox/${cc}/${pn}/insights`);
  return response.data;
}

export async function sendInboxMessage(payload: {
  countryCode: string;
  phoneNumber: string;
  templateName: string;
  languageCode?: string;
  bodyValues?: string[];
  headerValues?: string[];
  headerMediaUrl?: string;
  headerMimeType?: string;
  headerFileName?: string;
  buttonValues?: Record<string, unknown> | null;
  contactName?: string;
  autoPersonalizeVariables?: number[];
  email?: string | null;
  city?: string | null;
  platform?: string | null;
  vendorKind?: string | null;
  recipientType?: string | null;
  recipientId?: string | null;
  tags?: string[];
}) {
  const response = await api.post(`${BASE}/inbox/send`, payload);
  return response.data;
}

export async function sendInboxBulkMessage(payload: {
  recipients: Array<{
    countryCode: string;
    phoneNumber: string;
    contactName?: string;
    displayName?: string;
    name?: string;
    email?: string | null;
    city?: string | null;
    platform?: string | null;
    vendorKind?: string | null;
    recipientType?: string | null;
    recipientId?: string | null;
    tags?: string[];
  }>;
  templateName: string;
  languageCode?: string;
  bodyValues?: string[];
  headerValues?: string[];
  headerMediaUrl?: string;
  headerMimeType?: string;
  headerFileName?: string;
  buttonValues?: Record<string, unknown> | null;
  autoPersonalizeVariables?: number[];
}) {
  const response = await api.post(`${BASE}/inbox/send-bulk`, payload);
  return response.data;
}

export async function browseWhatsAppSendRecipients(params: {
  source?: 'contacts' | 'users' | 'vendors';
  platform?: 'all' | 'web' | 'ios' | 'android';
  vendorKind?: 'all' | 'doctor' | 'ambulance';
  q?: string;
  limit?: number;
  offset?: number;
}) {
  const response = await api.get<{
    success: boolean;
    data: WhatsAppRecipientPreview[];
    meta?: { total: number; hasMore: boolean };
  }>(`${BASE}/inbox/recipients/browse`, { params });
  return response.data;
}

export async function searchWhatsAppSendRecipients(
  q: string,
  type: 'all' | 'user' | 'vendor' | 'doctor' = 'all',
  opts?: { platform?: string; vendorKind?: string }
) {
  const response = await api.get<{ success: boolean; data: WhatsAppRecipientPreview[] }>(
    `${BASE}/inbox/contacts/search`,
    { params: { q, type, limit: 30, platform: opts?.platform, vendorKind: opts?.vendorKind } }
  );
  return response.data;
}

export async function updateInboxContact(
  countryCode: string,
  phoneNumber: string,
  payload: { tags?: string[]; notes?: string; displayName?: string }
) {
  const cc = encodeURIComponent(countryCode);
  const pn = encodeURIComponent(phoneNumber);
  const response = await api.put(`${BASE}/inbox/${cc}/${pn}/contact`, payload);
  return response.data;
}

export async function fetchWhatsAppAnalytics(
  params: {
    days?: number;
    source?: string;
    eventType?: string;
    templateName?: string;
    messageCategory?: string;
    recipientType?: string;
  } = {}
) {
  const response = await api.get<{ success: boolean; data: import('../../models/MasterAdmin/whatsappHub').WhatsAppAnalytics }>(
    `${BASE}/inbox/analytics`,
    { params }
  );
  return response.data;
}

export async function backfillWhatsAppInbox() {
  const response = await api.post(`${BASE}/inbox/backfill`);
  return response.data;
}

// ─── Contacts ────────────────────────────────────────────────────────────────

export async function fetchWhatsAppContacts(params?: {
  search?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppContact[];
    pagination?: {
      total: number;
      limit: number;
      offset: number;
      page?: number;
      totalPages?: number;
    };
    overview?: {
      totalContacts: number;
      totalTags: number;
      linkedUsers: number;
      linkedVendors: number;
      withMessages: number;
      tags: string[];
    };
  }>(`${BASE}/contacts`, { params });
  return response.data;
}

export async function fetchWhatsAppContact(id: string) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppContact;
  }>(`${BASE}/contacts/${id}`);
  return response.data;
}

export async function createWhatsAppContact(payload: {
  displayName?: string;
  countryCode?: string;
  phoneNumber: string;
  tags?: string[];
  notes?: string;
}) {
  const response = await api.post(`${BASE}/contacts`, payload);
  return response.data;
}

export async function updateWhatsAppContact(
  id: string,
  payload: {
    displayName?: string;
    countryCode?: string;
    phoneNumber?: string;
    tags?: string[];
    notes?: string;
  }
) {
  const response = await api.put(`${BASE}/contacts/${id}`, payload);
  return response.data;
}

export async function deleteWhatsAppContact(id: string) {
  const response = await api.delete(`${BASE}/contacts/${id}`);
  return response.data;
}

export async function importWhatsAppContacts(
  rows: Record<string, string>[],
  options?: { defaultTags?: string[] | string }
) {
  const response = await api.post(`${BASE}/contacts/import`, {
    rows,
    defaultTags: options?.defaultTags,
  });
  return response.data;
}

// ─── Test send ───────────────────────────────────────────────────────────────

export async function fetchWhatsAppTestSendContext(params: {
  mode: import('../../models/MasterAdmin/whatsappHub').WhatsAppTestSendMode;
  templateName?: string;
  eventKey?: string;
  campaignId?: string;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppTestSendContext;
  }>(`${BASE}/test-send/context`, { params });
  return response.data.data;
}

export async function sendWhatsAppTestMessage(payload: {
  mode: import('../../models/MasterAdmin/whatsappHub').WhatsAppTestSendMode;
  countryCode?: string;
  phoneNumber: string;
  contactName?: string;
  templateName?: string;
  eventKey?: string;
  campaignId?: string;
  languageCode?: string;
  bodyValues?: string[];
  headerValues?: string[];
  headerMediaUrl?: string;
  headerMimeType?: string;
  headerFileName?: string;
  buttonValues?: Record<string, string[]> | null;
  buttonUrls?: Record<string, string>;
  autoPersonalizeVariables?: number[];
  recipientType?: string | null;
  recipientId?: string | null;
  context?: Record<string, string>;
}) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppTestSendResult;
  }>(`${BASE}/test-send`, payload);
  return response.data;
}

export async function fetchWhatsAppTestSendStatus(deliveryLogId: string) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppDeliveryLog;
  }>(`${BASE}/test-send/status/${deliveryLogId}`);
  return response.data.data;
}

export async function fetchRecentWhatsAppTestSends(params?: {
  limit?: number;
  templateName?: string;
  eventType?: string;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppDeliveryLog[];
  }>(`${BASE}/test-send/recent`, { params });
  return response.data.data || [];
}

export async function fetchWhatsAppTestAnalytics(params?: {
  days?: number;
  templateName?: string;
  eventType?: string;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppAnalytics & {
      recentTestSends: import('../../models/MasterAdmin/whatsappHub').WhatsAppDeliveryLog[];
    };
  }>(`${BASE}/test-send/analytics`, { params });
  return response.data.data;
}

export async function fetchRcsTestStatus() {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').RcsHubStatus;
  }>(`${BASE}/rcs/status`);
  return response.data.data;
}

export async function fetchRcsStatus() {
  return fetchRcsTestStatus();
}

export async function fetchRcsTemplates() {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').RcsTemplate[];
    meta?: { source?: string; error?: string | null };
  }>(`${BASE}/rcs/templates`);
  return response.data;
}

export async function fetchRcsLogs(params?: { limit?: number; source?: string; status?: string }) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').RcsDeliveryLog[];
    pagination?: { total: number; limit: number; offset: number };
  }>(`${BASE}/rcs/logs`, { params });
  return response.data;
}

export async function sendRcsTestMessage(payload: {
  countryCode?: string;
  phoneNumber: string;
  templateName?: string;
  bodyValues?: string[];
  headerValues?: string[];
  buttonValues?: Record<string, unknown> | null;
  languageCode?: string;
  dryRun?: boolean;
  contactName?: string;
  recipientType?: string;
  recipientId?: string;
}) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    data: {
      sent: boolean;
      dryRun?: boolean;
      reason?: string;
      deliveryLogId?: string;
      interaktMessageId?: string | null;
      phone?: string;
      rcsTemplateName?: string;
      payloadPreview?: unknown;
      config?: {
        enabled: boolean;
        ready: boolean;
        blockers: string[];
        note: string;
      };
      raw?: unknown;
    };
  }>(`${BASE}/test-send/rcs`, payload);
  return response.data;
}

export async function sendRcsMessage(payload: {
  countryCode: string;
  phoneNumber: string;
  templateName: string;
  languageCode?: string;
  bodyValues?: string[];
  headerValues?: string[];
  buttonValues?: Record<string, unknown> | null;
  contactName?: string;
  recipientType?: string | null;
  recipientId?: string | null;
  dryRun?: boolean;
}) {
  const response = await api.post(`${BASE}/rcs/send`, payload);
  return response.data;
}

export async function sendRcsBulkMessage(payload: {
  recipients: Array<{
    countryCode: string;
    phoneNumber: string;
    contactName?: string;
    displayName?: string;
    name?: string;
    recipientType?: string | null;
    recipientId?: string | null;
  }>;
  templateName: string;
  languageCode?: string;
  bodyValues?: string[];
  headerValues?: string[];
  buttonValues?: Record<string, unknown> | null;
}) {
  const response = await api.post(`${BASE}/rcs/send-bulk`, payload);
  return response.data;
}

export { fetchWhatsAppDeliveryLogs, fetchWhatsAppWebhookConfig } from './whatsappDeliveryService';
